import { ChevronRight } from "@mui/icons-material";
import { Box, Button, Divider, Grid2, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { GqlDashboardFieldworkerNextShift } from "~/__generated__";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDate } from "~/modules/app/util/formatters";

import NextShiftTimeInfo from "./NextShiftTimeInfo";
import NextShiftTravelInfo from "./NextShiftTravelInfo";

type NextShiftProps = {
    component: GqlDashboardFieldworkerNextShift;
};

export default function DashboardFieldworkerNextShift({ component }: NextShiftProps) {
    const { t } = useTranslation("app");
    const plannerShift = component?.plannerShift?.data?.[0];

    return (
        <Grid2 size={{ xs: 12, lg: 6 }}>
            <Paper variant="outlined" sx={{ padding: 2 }}>
                <Stack direction="row" justifyContent="space-between" paddingBottom={2}>
                    <Typography variant="h4">{t("dashboardComponents.dashboardFieldworkerNextShift.nextShift")}</Typography>
                    <Button endIcon={<ChevronRight />} variant="text" component={Link} to={"/fieldworker/shifts"} size="small">
                        {t("dashboardComponents.dashboardFieldworkerNextShift.btnGoToShifts")}{" "}
                    </Button>
                </Stack>
                {plannerShift !== undefined ? <PopulatedComponent element={plannerShift} /> : <EmptyComponent />}
            </Paper>
        </Grid2>
    );
}

function EmptyComponent() {
    const { t } = useTranslation("app");

    return <Typography>{t("dashboardComponents.dashboardFieldworkerNextShift.noUpcomingShift")}</Typography>;
}

type PopulatedComponentProps = {
    element: GqlDashboardFieldworkerNextShift["plannerShift"]["data"][number];
};

function PopulatedComponent({ element }: PopulatedComponentProps) {
    const startTime = parseStringToDate(element.startTime);
    const endTime = parseStringToDate(element.endTime);

    return (
        <Box>
            <Typography variant="subtitle2">{formatDate(startTime)}</Typography>
            <Typography color="primary.main" variant="h5">
                {element.summary.plannerAreaHeading}
            </Typography>
            <Typography variant="body1">{element.summary.plannerAreaAddress}</Typography>
            <Typography color="text.secondary" variant="subtitle1">
                {element.summary.protocolHeading}
            </Typography>
            <Stack direction="column" paddingTop={1} rowGap={1}>
                <NextShiftTimeInfo startAt={startTime} endAt={endTime} />
                <Divider orientation="horizontal" />
                <NextShiftTravelInfo travelType={element.summary.travelMethod} />
            </Stack>
        </Box>
    );
}
